import { Client } from '@microsoft/microsoft-graph-client';
import { IAppContext } from 'App/AppContext';
import axios from 'axios';
import { classifySharePointUrl, SharePointUrlClassification } from 'components/SharePoint/SharePointUrlHelper';
import { globalMaxSharePointListSize } from 'globalConstants';
import { Drive, DriveItem } from 'microsoft-graph';
import { GraphScopeId } from 'services/Auth/authConfig';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { getExternalUrl } from 'utils/url';
import { graphUploadCancelledCode, graphUploadEmptyFileCode, graphUploadIncompleteCode } from './graphErrors';
import { execDelete, execGet, execPost, execPut } from './graphServiceCore';
import { ICreateUploadSessionResponse, IDriveItem, IDriveItemVersion, IList } from './SharepointInterfaces';

export const fileUploadMultipleOf = 4194304; //4 MB
export let cancelAsyncProcesState = false;
export const driveItemFields =
  'id,name,lastModifiedDateTime,publication,parentReference,root,folder,size,sharepointIds,webUrl';

//
// Global
//
const publishedLevel: string = 'published';

export function cancelGraphAsyncProces(state: boolean) {
  cancelAsyncProcesState = state;
}

//Batch callers upload one file per iteration. A cancel raised between two files is only
//observable by reading the flag here, because each upload call clears it once it acts on it.
export function isGraphAsyncProcesCancelled(): boolean {
  return cancelAsyncProcesState;
}

//
// Sharepoint Drives
//
export const getValidFilename = (filename: string): string => {
  const invalidChars = '"*:<>?/\\|\'#%';
  let newFilename = filename;

  for (const char of invalidChars) {
    newFilename = newFilename.replaceAll(char, '_');
  }

  return newFilename.trim();
};

export const graphGetDrivesForSite = async (client: Client, siteId: string): Promise<Drive[]> => {
  try {
    const request = `/sites/${siteId}/drives`;
    logger.debug('graphGetDriveRoot', request);
    const res = await execGet(client.api(request).select('id,name,webUrl,driveType'));

    if (res && res.value) {
      return res.value;
    } else {
      return [];
    }
  } catch (err) {
    logger.debug('graphGetDrivesForSite', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphCreateDrive = async (client: Client, siteId: string, name: string): Promise<IDriveItem> => {
  try {
    const newList: IList = {
      displayName: name,
      list: {
        template: 'documentLibrary',
      },
    };

    logger.debug('graphCreateDrive', newList);
    const createdList: IList = await execPost(client.api(`sites/${siteId}/lists`), newList);
    const drive: Drive = await execGet(client.api(`sites/${siteId}/lists/${createdList.id}/drive`));

    if (drive) {
      return drive;
    } else {
      throw new AppError('Could not get drive root from newly created document library');
    }
  } catch (err) {
    logger.debug('graphCreateDrive', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetDriveRoot = async (client: Client, driveId: string): Promise<IDriveItem> => {
  try {
    const request = `/drives/${driveId}/root`;
    logger.debug('graphGetDriveRoot', request);
    const res = await execGet(client.api(request).select(driveItemFields));

    return res;
  } catch (err) {
    logger.debug('graphGetDriveRoot', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetChildrenForDrive = async (client: Client, driveId: string): Promise<IDriveItem[]> => {
  try {
    const request = `/drives/${driveId}/root/children`;
    logger.debug('graphGetChildrenForDrive', request);
    let res = await execGet(client.api(request).select(driveItemFields));
    let items: IDriveItem[] = [];

    if (res && res.value) {
      let nextLink = res['@odata.nextLink'];
      items = [...res.value];

      while (nextLink) {
        logger.debug('graphGetChildrenForDrive', nextLink);
        res = await execGet(client.api(nextLink));
        if (!res) break;
        if (res.value) {
          items.push(...res.value);
        }
        nextLink = res['@odata.nextLink'];
        if (items.length > globalMaxSharePointListSize) break;
      }
    }

    return items;
  } catch (err) {
    logger.debug('graphGetChildrenForDriveItem', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetChildrenForDriveItemBatchUrl = (
  driveId: string | undefined,
  itemId?: string | undefined,
): string | undefined => {
  if (!driveId) return undefined;
  if (!itemId) {
    return `/drives/${driveId}/root/children`;
  } else {
    return `/drives/${driveId}/items/${itemId}/children`;
  }
};

export const graphGetChildrenForDriveItem = async (
  client: Client,
  driveId: string,
  itemId: string,
): Promise<IDriveItem[]> => {
  try {
    const request = `/drives/${driveId}/items/${itemId}/children`;
    logger.debug('graphGetChildrenForDriveItem', request);
    let res = await execGet(client.api(request).select(driveItemFields));
    let items: IDriveItem[] = [];

    if (res && res.value) {
      let nextLink = res['@odata.nextLink'];
      items = [...res.value];

      while (nextLink) {
        logger.debug('graphGetChildrenForDriveItem', nextLink);
        res = await execGet(client.api(nextLink));
        if (!res) break;
        if (res.value) {
          items.push(...res.value);
        }
        nextLink = res['@odata.nextLink'];
        if (items.length > globalMaxSharePointListSize) break;
      }
    }

    return items;
  } catch (err) {
    logger.debug('graphGetChildrenForDriveItem', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphCreateNewFolderInDrive = async (
  client: Client,
  driveId: string,
  driveItemId: string,
  folderName: string,
): Promise<IDriveItem> => {
  try {
    const driveItem = {
      name: folderName.trim(),
      folder: {},
      '@microsoft.graph.conflictBehavior': 'rename',
    };

    logger.debug('graphCreateNewFolderInDrive', driveItem);
    const res = await execPost(client.api(`/drives/${driveId}/items/${driveItemId}/children`), driveItem);

    return res;
  } catch (err) {
    logger.debug('graphCreateNewFolderInDrive', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetDrive = async (client: Client, driveId: string): Promise<Drive | undefined> => {
  try {
    const request = `/drives/${driveId}`;
    logger.debug('graphGetDrive', request);
    const drive: Drive = await execGet(client.api(request));

    return drive;
  } catch (err) {
    logger.debug('graphGetDrive', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetDriveItemForPath = async (
  client: Client,
  driveId: string,
  path: string,
  fileName: string,
): Promise<IDriveItem | undefined> => {
  try {
    if (path.startsWith('/')) path = path.substring(1);
    if (path.endsWith('/')) path = path.slice(0, -1);

    let request = `/drives/${driveId}/root:/${path}/${fileName}`;
    request = encodeURI(request);
    const item: DriveItem = await execGet(client.api(request).select(driveItemFields));

    return item;
  } catch (err) {
    logger.debug('graphGetDriveItemForPath', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetDriveItemPath = async (
  client: Client,
  item: DriveItem,
  driveId: string,
  driveItemId: string | undefined,
  skipFirstItem: boolean,
): Promise<string | undefined> => {
  try {
    let path = '';

    if (item) {
      if (!skipFirstItem && item.folder && !item.root) {
        path = '/' + item.name;
      }

      while (item.parentReference?.id) {
        const parentRequest = `/drives/${driveId}/items/${item.parentReference?.id}`;
        const parent: DriveItem = await execGet(client.api(parentRequest));

        //stop at the level of the list, indicated with driveItemId
        if (parent.id === driveItemId) break;

        //or stop at the root of the site
        if (parent.folder && !parent.root) {
          path = '/' + parent.name + path;
          item = parent;
        } else {
          break;
        }
      }
    }

    return path;
  } catch (err) {
    logger.debug('graphGetDriveItemPath', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetDriveItemBatchUrl = (
  driveId: string | undefined,
  itemId?: string | undefined,
): string | undefined => {
  if (!driveId || !itemId) return undefined;

  return `/drives/${driveId}/items/${itemId}`;
};

export const graphGetDriveItem = async (
  client: Client,
  driveId: string,
  driveItemId: string,
): Promise<IDriveItem | undefined> => {
  try {
    const request = `/drives/${driveId}/items/${driveItemId}`;
    logger.debug('graphGetDriveItem', request);
    const item: DriveItem = await execGet(client.api(request).select(driveItemFields));

    return item;
  } catch (err) {
    logger.debug('graphGetDriveItem', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetIDriveItemVersions = async (
  client: Client,
  driveId: string,
  driveItemId: string,
): Promise<IDriveItemVersion[]> => {
  try {
    //
    // Returns all versions for a drive item
    // Ordered from latest to oldest
    //
    const request = `/drives/${driveId}/items/${driveItemId}/versions`;
    logger.debug('graphGetIDriveItemVersions', request);
    const item = await execGet(client.api(request));
    if (item && item.value && item.value.length > 0) {
      return item.value;
    }

    return [];
  } catch (err) {
    logger.debug('graphGetIDriveItemVersion', err);

    return [];
  }
};

export const graphGetDocumentLibraryItemThumbnails = async (
  client: Client,
  driveId: string,
  driveItemId: string,
): Promise<string | undefined> => {
  try {
    let url = undefined;
    const request = `/drives/${driveId}/items/${driveItemId}/thumbnails?select=small`;
    logger.debug('graphGetDocumentLibraryItemThumbnails', request);
    const item = await execGet(client.api(request));

    if (item && item.value && item.value.length > 0) {
      url = item.value[0].small.url;
    } else {
      logger.debug('graphGetDocumentLibraryItemThumbnails. Could not get valid response.', item);
    }

    return url;
  } catch (err) {
    logger.debug('graphGetDocumentLibraryItemThumbnails', err);

    return undefined;
  }
};

export const graphGetDriveItemContent = async (client: Client, driveId: string, driveItemId: string): Promise<Blob> => {
  try {
    const request = `/drives/${driveId}/items/${driveItemId}`;
    logger.debug('graphGetDriveItemContent', request);
    const item = await execGet(client.api(request).select('content.downloadUrl'));
    const stream = await fetch(item['@microsoft.graph.downloadUrl']);
    const blob = await stream.blob();

    return blob;
  } catch (err) {
    logger.debug('graphGetDriveItemContent', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetDriveItemContentAsText = async (
  client: Client,
  driveId: string,
  driveItemId: string,
): Promise<string> => {
  try {
    const request = `/drives/${driveId}/items/${driveItemId}`;
    logger.debug('graphGetDocumentLibraryItemContent', request);
    const item = await execGet(client.api(request).select('content.downloadUrl'));
    const stream = await fetch(item['@microsoft.graph.downloadUrl']);
    const content = await stream.text();

    return content;
  } catch (err) {
    logger.debug('graphGetDocumentLibraryItemContent', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetDriveItemPreview = async (
  client: Client,
  driveId: string,
  driveItemId: string,
): Promise<string | undefined> => {
  try {
    const options = {
      zoom: 1.1,
    };

    let url = undefined;
    const request = `/drives/${driveId}/items/${driveItemId}/preview`;
    logger.debug('graphGetDocumentLibraryItemPreview', request);
    const item = await execPost(client.api(request), options);

    if (item && item.getUrl) {
      url = item.getUrl + '&nb=true';
    } else {
      logger.debug('graphGetDocumentLibraryItemPreview. Could not get valid response.', item);
    }

    return url;
  } catch (err) {
    logger.debug('graphGetDocumentLibraryItemPreview', err);

    return undefined;
  }
};

export const graphGetDriveItemEmbedLink = async (
  client: Client,
  driveId: string,
  driveItemId: string,
): Promise<string | undefined> => {
  try {
    const permission = {
      type: 'view',
      scope: 'organization',
    };

    const request = `/drives/${driveId}/items/${driveItemId}/createLink`;
    logger.debug('graphGetDocumentLibraryItemEmbedLink', request);
    const item = await execPost(client.api(request), permission);

    if (item && item.link && item.link.webUrl) {
      return item.link.webUrl;
    } else {
      logger.debug('graphGetDocumentLibraryItemEmbedLink. Could not get valid response.', item);
    }

    return undefined;
  } catch (err) {
    logger.debug('graphGetDocumentLibraryItemEmbedLink', err);

    return undefined;
  }
};

//
// Sharepoint upload files
//
export const graphUpdateFile = async (
  client: Client,
  file: File,
  driveId: string,
  driveItemId: string,
): Promise<IDriveItem | undefined> => {
  try {
    const res: DriveItem = await execPut(client.api(`/drives/${driveId}/items/${driveItemId}/content`), file);

    return res;
  } catch (err) {
    logger.debug('graphUpdateFile', err);

    throw AppError.fromGraphError(err);
  }
};

export type onUploadProgress = (totalBytes: number, sendBytes: number) => void;

//A chunk PUT either completes the upload session, in which case Sharepoint answers with the
//new DriveItem, or it acknowledges the chunk with a 202 that lists the ranges still expected.
interface IUploadChunkResult {
  item?: IDriveItem;
  nextExpectedRanges?: string[];
}

//Sharepoint can ask for a chunk to be sent again by repeating its offset in
//nextExpectedRanges. Bound those resends so a session that never advances ends in a
//reported failure instead of looping forever.
const maxChunkResends = 3;

/**
 * Uploads a file to a Sharepoint folder and returns the created DriveItem.
 * Never resolves to undefined: an upload that does not produce a DriveItem throws an
 * AppError carrying graphUploadCancelledCode (the user cancelled) or
 * graphUploadIncompleteCode (the session ended without completing). Empty files throw
 * graphUploadEmptyFileCode before any Graph request is created.
 */
export const graphUploadFileToFolder = async (
  client: Client,
  file: File,
  driveId: string,
  parentFolderId: string,
  fileName: string,
  description?: string,
  onProgress?: onUploadProgress,
  conflictBehavior?: string,
): Promise<IDriveItem> => {
  try {
    fileName = fileName.trim();

    // An upload session cannot carry a zero length chunk. Empty files are invalid input,
    // and a direct content PUT would ignore conflictBehavior and may replace an existing file.
    if (file.size === 0) {
      throw new AppError('graphUploadFileToFolder: empty files cannot be uploaded', graphUploadEmptyFileCode);
    }

    const request = `/drives/${driveId}/items/${parentFolderId}:/${encodeURIComponent(fileName)}:/createUploadSession`;
    logger.debug('graphUploadFileToFolder', request);

    const res: ICreateUploadSessionResponse = await client.api(request).post({
      name: fileName,
      description: description,
      item: {
        '@microsoft.graph.conflictBehavior': conflictBehavior ?? 'rename',
      },
    });

    if (!res || !res.uploadUrl) {
      throw new AppError('graphUploadFileToFolder: no upload session was returned', graphUploadIncompleteCode);
    }

    if (onProgress) onProgress(file.size, 0);

    let chunkStart = 0;
    let resendsAtOffset = 0;

    while (chunkStart < file.size) {
      const chunkEnd = Math.min(chunkStart + fileUploadMultipleOf, file.size);
      const tmpSlice = file.slice(chunkStart, chunkEnd);
      const response = await uploadSinglePart(res.uploadUrl, tmpSlice, file.size, chunkStart, chunkEnd);

      // Return-on-completion must come before the cancel check.
      // SharePoint's chunked upload protocol auto-completes the upload session
      // the moment the final chunk PUT succeeds -- the session URL is 404 from
      // that point on. If the user clicked Cancel during the final chunk's
      // network roundtrip, cancelAsyncProcesState is true here even though the
      // upload succeeded; checking the cancel branch first would issue a DELETE
      // against an already-deleted session and surface that 404 to the user as
      // "Request failed with status code 404" despite the file being in
      // SharePoint. Returning the response first avoids that case entirely.
      // The cancel flag is deliberately left set here so a batch caller can still
      // see it and stop before the next file.
      if (response.item) return response.item;

      if (cancelAsyncProcesState) {
        try {
          await axios.delete(res.uploadUrl);
        } catch {
          // Cleanup of an aborted upload session is best-effort. The session may
          // already have expired (network race, slow server, mid-flight retry)
          // and no caller depends on the DELETE succeeding -- so swallow any
          // error here rather than let it surface to the user.
        }
        cancelGraphAsyncProces(false);

        throw new AppError('graphUploadFileToFolder: the upload was cancelled', graphUploadCancelledCode);
      }

      // Sharepoint reports the byte it wants next. Trust it over our own bookkeeping so a
      // chunk it did not accept is sent again instead of being skipped, which would leave a
      // partially written file behind.
      const nextChunkStart = getNextChunkStart(response.nextExpectedRanges) ?? chunkEnd;

      if (nextChunkStart > chunkStart) {
        resendsAtOffset = 0;
        if (onProgress) onProgress(file.size, nextChunkStart);
      } else {
        resendsAtOffset++;
        if (resendsAtOffset > maxChunkResends) {
          throw new AppError(
            `graphUploadFileToFolder: the upload stalled at byte ${chunkStart} of ${file.size}`,
            graphUploadIncompleteCode,
          );
        }
      }

      chunkStart = nextChunkStart;
    }

    // Every byte was sent but the session never answered with the DriveItem, so there is no
    // item to hand back. The file may exist in Sharepoint in an incomplete state.
    throw new AppError(
      'graphUploadFileToFolder: the upload finished without returning a drive item',
      graphUploadIncompleteCode,
    );
  } catch (err) {
    logger.debug('graphUploadFileToFolder', err);

    if (err instanceof AppError) throw err;

    throw AppError.fromGraphError(err);
  }
};

const uploadSinglePart = async (
  url: string,
  bytes: number[] | Blob,
  filesize: number,
  chunkStart: number,
  chunkEnd: number,
): Promise<IUploadChunkResult> => {
  try {
    const newtworkCall = await axios.put(url, bytes, {
      headers: {
        'Content-Range': 'bytes ' + chunkStart + '-' + (chunkEnd - 1) + '/' + filesize,
      },
    });

    if (newtworkCall.status === 202) {
      return { nextExpectedRanges: newtworkCall.data?.nextExpectedRanges };
    }

    return { item: newtworkCall.data as DriveItem };
  } catch (err) {
    logger.debug('uploadSinglePart', err);

    throw AppError.fromGraphError(err);
  }
};

//Ranges are formatted as "12345-" or "12345-67890". Only the start of the first range is
//needed: it is the next byte Sharepoint expects.
const getNextChunkStart = (nextExpectedRanges: string[] | undefined): number | undefined => {
  const firstRange = nextExpectedRanges?.[0];
  if (!firstRange) return undefined;

  const rangeStart = Number.parseInt(firstRange.split('-')[0], 10);

  return Number.isFinite(rangeStart) ? rangeStart : undefined;
};

//
// Helpers
//
export const graphCheckWriteAccessInDocumentLibrary = async (client: Client, driveId: string): Promise<boolean> => {
  try {
    const driveRoot = await graphGetDriveRoot(client, driveId);
    if (driveRoot && driveRoot.id) {
      const data: BlobPart[] = ['dummyData'];
      const file = new File(data, 'dummyFile.txt');

      const newDriveItem = await graphUploadFileToFolder(client, file, driveId, driveRoot.id, file.name);
      if (newDriveItem && newDriveItem.id) {
        try {
          await graphDeleteFile(client, driveId, newDriveItem.id);
        } catch {
          //ignore
        }
      }
    }

    return true;
  } catch (err) {
    logger.debug('graphCheckWriteAccessInDocumentLibrary', err);

    return false;
  }
};

export const graphDeleteFile = async (client: Client, driveId: string, driveItemId: string): Promise<void> => {
  try {
    const path = `/drives/${driveId}/items/${driveItemId}`;
    logger.debug('graphValidateDocumentLibraryItem', path);
    await execDelete(client.api(path).header('Prefer', 'bypass-shared-lock'));
  } catch (err) {
    logger.debug('graphDeleteFile', err);
    const appErr = AppError.fromGraphError(err);
    if (appErr.code?.toLowerCase() === 'itemNotFound') {
      return; //oke
    }
    if (appErr.code?.toLowerCase() === 'notAllowed') {
    } else {
      throw appErr;
    }
  }
};

export const graphValidateDriveItem = async (
  client: Client,
  driveId: string,
  driveItemId: string,
): Promise<boolean> => {
  try {
    const path = `/drives/${driveId}/items/${driveItemId}`;
    logger.debug('graphValidateDocumentLibraryItem', path);
    await execGet(client.api(path));

    return true;
  } catch (err) {
    logger.debug('graphValidateDocumentLibraryItem', err);

    return false;
  }
};

export const graphPublishDriveItem = async (client: Client, driveId: string, driveItemId: string): Promise<void> => {
  try {
    //first checkout
    let path = `/drives/${driveId}/items/${driveItemId}/checkout`;
    logger.debug('graphPublishDriveItem checkout', path);
    await execPost(client.api(path), null);
    //checkin with published option
    const options = {
      checkInAs: publishedLevel,
      comment: '',
    };
    path = `/drives/${driveId}/items/${driveItemId}/checkin`;
    logger.debug('graphPublishDriveItem checkin', path);
    await execPost(client.api(path), options);
  } catch (err) {
    logger.debug('graphPublishDriveItem', err);
    throw AppError.fromGraphError(err);
  }
};

export interface IUrlFileResult {
  url: string | undefined;
  isUrlFile: boolean;
  urlClassification?: SharePointUrlClassification;
}

export const graphGetDriveItemUrl = async (
  appContext: IAppContext,
  driveId: string | undefined,
  driveItemId: string | undefined,
  driveItemWebUrl: string | undefined,
): Promise<IUrlFileResult | undefined> => {
  const result: IUrlFileResult = { url: getExternalUrl(driveItemWebUrl || '', '', ''), isUrlFile: false };
  if (!driveItemWebUrl) return result;

  if (result.url?.toLowerCase()?.endsWith('.url')) {
    //special handling for .url files - download the file and return to the URL contained in the file
    result.isUrlFile = true;
    result.url = undefined;
    if (driveId && driveItemId) {
      try {
        const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library);
        if (!graphInterface.accessToken) return result;
        const content = await graphGetDriveItemContentAsText(graphInterface.client, driveId, driveItemId);
        const match = content.match(/URL=([^\n\\]+)/);
        if (match && match[1]) {
          result.url = match[1].trim();
          result.urlClassification = classifySharePointUrl(result.url);
        }
      } catch (error) {
        logger.debug('openDriveItemUrl: Error fetching .url file', error);
      }
    }
  }

  return result;
};

const graphGetShareToken = (url: string): string => {
  // Encode to UTF-8 bytes
  const bytes = new TextEncoder().encode(url);

  // Convert to binary string
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  // Base64 encode
  const b64 = window.btoa(binary);

  // Convert to base64url (no padding, no +, /)
  const b64url = b64.replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');

  return `u!${b64url}`;
};

export const graphGetDriveItemForUrl = async (client: Client, targetUrl: string): Promise<IDriveItem | undefined> => {
  try {
    const token = graphGetShareToken(targetUrl);
    const path = `https://graph.microsoft.com/v1.0/shares/${token}/driveItem`;
    const item = await execGet(client.api(path).select(driveItemFields));

    return item;
  } catch (err) {
    logger.debug('graphGetDriveItemForUrl', err);
    throw AppError.fromGraphError(err);
  }
};
